import { useEffect, useMemo, useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/i18n/format";
import {
  useSchedulingConfig,
  useUpdateSchedulingConfig,
  type SchedulingConfig,
} from "@/hooks/use-scheduling-config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PostingWindowGrid } from "@/components/settings/PostingWindowGrid";
import { toast } from "sonner";
import type { SocialPlatform } from "@content-os/config";
import { POST_TYPE_REGISTRY, VIDEO_COMPANION_POST_TYPES } from "@content-os/post-types";

const PLATFORMS: SocialPlatform[] = ["INSTAGRAM", "TIKTOK", "TWITTER", "LINKEDIN", "FACEBOOK", "YOUTUBE", "THREADS", "PINTEREST"];

function getAllTimezones(): string[] {
  const supported = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : null;
  return supported && supported.length > 0
    ? supported
    : [
        "UTC",
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "Europe/London",
        "Europe/Berlin",
        "Asia/Tokyo",
      ];
}

function getDetectedTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function getOffsetMinutes(tz: string, ref: Date): number {
  // Format the reference date in the target tz to derive its offset from UTC.
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = dtf.formatToParts(ref);
    const lookup: Record<string, string> = {};
    for (const p of parts) if (p.type !== "literal") lookup[p.type] = p.value;
    const asUTC = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      Number(lookup.hour),
      Number(lookup.minute),
      Number(lookup.second),
    );
    return Math.round((asUTC - ref.getTime()) / 60000);
  } catch {
    return 0;
  }
}

function formatOffset(minutes: number): string {
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0
    ? `UTC${sign}${h}`
    : `UTC${sign}${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function groupOf(tz: string): string {
  if (tz === "UTC") return "UTC";
  const idx = tz.indexOf("/");
  return idx === -1 ? "Other" : tz.slice(0, idx);
}

interface GroupedTimezones {
  detected: string;
  groups: Array<{ name: string; items: Array<{ id: string; label: string; offset: number }> }>;
}

function buildGroupedTimezones(now: Date): GroupedTimezones {
  const detected = getDetectedTimezone();
  const all = getAllTimezones();
  const byGroup = new Map<string, Array<{ id: string; label: string; offset: number }>>();
  for (const id of all) {
    const offset = getOffsetMinutes(id, now);
    const label = `${id.replace(/_/g, " ")} (${formatOffset(offset)})`;
    const g = groupOf(id);
    const arr = byGroup.get(g) ?? [];
    arr.push({ id, label, offset });
    byGroup.set(g, arr);
  }
  const groupOrder = ["UTC", "America", "Europe", "Africa", "Asia", "Australia", "Pacific", "Atlantic", "Indian", "Antarctica", "Arctic", "Etc", "Other"];
  const groups = Array.from(byGroup.entries())
    .sort((a, b) => {
      const ai = groupOrder.indexOf(a[0]);
      const bi = groupOrder.indexOf(b[0]);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map(([name, items]) => ({
      name,
      items: items.sort((a, b) => a.offset - b.offset || a.id.localeCompare(b.id)),
    }));
  return { detected, groups };
}

export function BrandSettingsSchedulingPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";

  const { data, isLoading } = useSchedulingConfig(brandId);
  const update = useUpdateSchedulingConfig(brandId);
  const [draft, setDraft] = useState<SchedulingConfig | null>(null);
  useEffect(() => { if (data) setDraft(data); }, [data]);

  const locale = useLocale();
  const tzData = useMemo(() => buildGroupedTimezones(new Date()), []);
  const currentInDraftTz = useMemo(() => {
    if (!draft?.timezone) return "—";
    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone: draft.timezone,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date());
    } catch {
      return "—";
    }
  }, [draft?.timezone, locale]);

  if (isLoading || !draft) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(data);

  async function save() {
    if (!draft) return;
    await update.mutateAsync(draft);
    toast.success(t("scheduling.saved"));
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("scheduling.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("scheduling.subtitle")}</p>
      </header>

      <section className="rounded-lg border p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("scheduling.timezone")}</h2>
          {tzData.detected && tzData.detected !== draft.timezone && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canManageBrand}
              onClick={() => setDraft({ ...draft, timezone: tzData.detected })}
            >
              {t("scheduling.useDetected", { tz: tzData.detected })}
            </Button>
          )}
        </div>
        <select
          value={draft.timezone}
          disabled={!canManageBrand}
          onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
          className="block w-full max-w-md rounded border px-3 py-2 text-sm"
        >
          {tzData.detected && (
            <optgroup label={t("scheduling.detectedFromBrowser")}>
              <option value={tzData.detected}>
                {tzData.detected.replace(/_/g, " ")} ({formatOffset(getOffsetMinutes(tzData.detected, new Date()))})
              </option>
            </optgroup>
          )}
          {tzData.groups.map((g) => (
            <optgroup key={g.name} label={g.name}>
              {g.items.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          {t("scheduling.timezoneNote")}
          {" "}{t("scheduling.currentTime")}<span className="font-mono">{currentInDraftTz}</span>.
        </p>
      </section>

      <section className="rounded-lg border p-4 space-y-3">
        <h2 className="text-lg font-semibold">{t("scheduling.perPlatformRules")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PLATFORMS.map((p) => {
            const rule = draft.platformRules[p] ?? { maxPerDay: 0, minGapHours: 0 };
            return (
              <div key={p} className="rounded border p-3 grid grid-cols-[1fr_auto_auto] items-center gap-2">
                <span className="text-sm font-medium">{p}</span>
                <label className="flex items-center gap-2 text-xs">
                  {t("scheduling.maxPerDay")}
                  <input
                    type="number"
                    min={0}
                    value={rule.maxPerDay}
                    disabled={!canManageBrand}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        platformRules: { ...draft.platformRules, [p]: { ...rule, maxPerDay: Number(e.target.value) } },
                      })
                    }
                    className="w-16 rounded border px-2 py-1"
                  />
                </label>
                <label className="flex items-center gap-2 text-xs">
                  {t("scheduling.gapHours")}
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={rule.minGapHours}
                    disabled={!canManageBrand}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        platformRules: { ...draft.platformRules, [p]: { ...rule, minGapHours: Number(e.target.value) } },
                      })
                    }
                    className="w-16 rounded border px-2 py-1"
                  />
                </label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-3">
        <h2 className="text-lg font-semibold">{t("scheduling.postingWindows")}</h2>
        <p className="text-xs text-muted-foreground">{t("scheduling.postingWindowsHint")}</p>
        <PostingWindowGrid
          value={draft.defaultWindows}
          onChange={(windows) => setDraft({ ...draft, defaultWindows: windows })}
          disabled={!canManageBrand}
        />
      </section>

      <section className="rounded-lg border p-4 space-y-3">
        <h2 className="text-lg font-semibold">{t("scheduling.videoCompanions")}</h2>
        <p className="text-xs text-muted-foreground">{t("scheduling.videoCompanionsHint")}</p>
        <div className="flex flex-wrap gap-4">
          {VIDEO_COMPANION_POST_TYPES.map((postType) => {
            const selected = draft.videoCompanionPostTypes ?? [];
            return (
              <label key={postType} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(postType)}
                  disabled={!canManageBrand}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      // Keep the canonical order so toggling back and forth is not "dirty".
                      videoCompanionPostTypes: VIDEO_COMPANION_POST_TYPES.filter((pt) =>
                        pt === postType ? e.target.checked : selected.includes(pt),
                      ),
                    })
                  }
                />
                {POST_TYPE_REGISTRY[postType].label}
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || update.isPending || !canManageBrand}>
          {update.isPending ? t("actions.saving", { ns: "common" }) : t("actions.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  );
}
